// ─── Scanner de code-barres — natif (expo-camera) ET web (@zxing) ─────────────
// Ouvre la caméra en plein écran, décode un code EAN/UPC et renvoie les chiffres.
//
//   <ScannerCodeBarres visible={!!scan} onClose={…} onScan={(code) => …} />
//
// • Natif (Expo Go, build) : expo-camera (CameraView).
// • Web (Safari, navigateurs) : @zxing lit le flux d'un <video> — fonctionne
//   dans Safari sur iPhone, sans build ni réinstallation.
// Chaque bibliothèque n'est chargée que sur SA plateforme (require paresseux),
// pour ne pas alourdir/casser l'autre.
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';

import { useUI, useT } from '@mop/core';

const EST_WEB = Platform.OS === 'web';

// Natif : expo-camera (présent dans Expo Go et les builds).
let CameraView = null, useCameraPermissions = null;
if (!EST_WEB) {
  try {
    const cam = require('expo-camera');
    CameraView = cam.CameraView;
    useCameraPermissions = cam.useCameraPermissions;
  } catch (_) { /* module absent → fallback saisie manuelle */ }
}
// Web : @zxing (lecture du <video> via getUserMedia).
let BrowserMultiFormatReader = null;
if (EST_WEB) {
  try { BrowserMultiFormatReader = require('@zxing/browser').BrowserMultiFormatReader; } catch (_) {}
}

const TYPES_BARRES = ['ean13', 'ean8', 'upc_a', 'upc_e', 'code128'];
const TYPES_QR = ['qr'];

// `mode` :
//   'barcode' (défaut) → codes-barres produits uniquement, renvoie les chiffres.
//   'mixte'            → codes-barres ET QR ; renvoie le TEXTE brut décodé
//                        (l'appelant filtre : QR d'emplacement vs code produit).
export default function ScannerCodeBarres({ visible, onClose, onScan, mode = 'barcode' }) {
  const UI = useUI();
  const t = useT();
  const st = useMemo(() => makeStyles(UI), [UI]);
  const mixte = mode === 'mixte';

  // Pas de <Modal> ici : le catalogue est DÉJÀ une modale, et iOS ne présente
  // pas une modale au-dessus d'une autre (elle attend la fermeture de la 1re).
  // On rend donc un CALQUE plein écran, monté à l'intérieur de la modale parente.
  if (!visible) return null;
  // La caméra (vue NATIVE) se compose au-dessus des vues JS sœurs : si la barre
  // de fermeture est rendue AVANT, la caméra l'intercepte et le ✕ ne réagit pas.
  // On rend donc la caméra d'abord, puis la barre en OVERLAY absolu, avec
  // zIndex + elevation pour la garder cliquable au-dessus de la caméra.
  return (
      <View style={[StyleSheet.absoluteFill, st.page]}>
        {EST_WEB
          ? <ScannerWeb st={st} onScan={onScan} onClose={onClose} mixte={mixte} />
          : <ScannerNatif st={st} onScan={onScan} onClose={onClose} mixte={mixte} />}
        <View style={[st.barre, st.barreOverlay, { paddingTop: 44 }]} pointerEvents="box-none">
          <TouchableOpacity onPress={onClose} hitSlop={{ top: 14, bottom: 14, left: 14, right: 14 }} style={st.fermer}>
            <Text style={{ fontSize: 22, color: '#fff' }}>✕</Text>
          </TouchableOpacity>
          <Text style={st.titre}>{mixte ? t('inventaire.scannerMixte') : t('inventaire.scanner')}</Text>
          <View style={{ width: 40 }} />
        </View>
      </View>
  );
}

// Normalise le texte décodé selon le mode : brut (mixte) ou chiffres (barcode).
function normaliserCode(data, mixte) {
  const brut = String(data || '').trim();
  return mixte ? brut : brut.replace(/[^0-9]/g, '');
}

// ── Natif : expo-camera ───────────────────────────────────────────
function ScannerNatif({ st, onScan, onClose, mixte }) {
  const t = useT();
  const dispo = !!CameraView && !!useCameraPermissions;
  const [permission, demander] = (useCameraPermissions ? useCameraPermissions() : [null, () => {}]);
  const verrou = useRef(false);

  useEffect(() => { verrou.current = false; }, []);
  useEffect(() => { if (dispo && permission && !permission.granted) demander(); }, [dispo, permission]);

  const surCode = ({ data }) => {
    if (verrou.current) return;
    const code = normaliserCode(data, mixte);
    if (!code) return;
    verrou.current = true;
    onScan?.(code);
  };

  if (dispo && permission?.granted) {
    return (
      <View style={st.camWrap}>
        <CameraView
          style={StyleSheet.absoluteFill}
          facing="back"
          barcodeScannerSettings={{ barcodeTypes: mixte ? [...TYPES_BARRES, ...TYPES_QR] : TYPES_BARRES }}
          onBarcodeScanned={surCode}
        />
        <View style={st.cadre} pointerEvents="none" />
        <Text style={st.aide}>{mixte ? t('inventaire.viseMixte') : t('inventaire.viseBarcode')}</Text>
      </View>
    );
  }
  return (
    <VideVue st={st} onClose={onClose}
      texte={!dispo ? t('inventaire.scanIndispoNatif')
        : (permission && !permission.granted ? t('inventaire.scanAutoriser') : t('inventaire.scanPreparation'))}
      bouton={dispo && permission && !permission.granted ? { label: t('inventaire.scanAutoriserBouton'), onPress: demander } : null}
    />
  );
}

// ── Web : @zxing sur un élément <video> ───────────────────────────
function ScannerWeb({ st, onScan, onClose, mixte }) {
  const t = useT();
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const verrou = useRef(false);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    if (!BrowserMultiFormatReader) { setErreur('indispo'); return undefined; }
    let vivant = true;
    const reader = new BrowserMultiFormatReader();
    (async () => {
      try {
        // caméra arrière si possible, sinon la première disponible.
        const controls = await reader.decodeFromVideoDevice(undefined, videoRef.current, (res) => {
          if (!vivant || verrou.current || !res) return;
          const code = normaliserCode(res.getText(), mixte);
          if (!code) return;
          verrou.current = true;
          onScan?.(code);
        });
        controlsRef.current = controls;
      } catch (e) {
        setErreur(e && e.name === 'NotAllowedError' ? 'refus' : 'reseau');
      }
    })();
    return () => {
      vivant = false;
      try { controlsRef.current?.stop(); } catch (_) {}
    };
  }, []);

  if (erreur) {
    return (
      <VideVue st={st} onClose={onClose}
        texte={erreur === 'refus'
          ? t('inventaire.scanRefusWeb')
          : erreur === 'indispo'
            ? t('inventaire.scanIndispoWeb')
            : t('inventaire.scanErreurWeb')}
      />
    );
  }
  // Élément vidéo DOM (react-native-web rend les balises string en HTML).
  return (
    <View style={st.camWrap}>
      {React.createElement('video', {
        ref: videoRef, autoPlay: true, playsInline: true, muted: true,
        style: { width: '100%', height: '100%', objectFit: 'cover', position: 'absolute' },
      })}
      <View style={st.cadre} pointerEvents="none" />
      <Text style={st.aide}>{mixte ? t('inventaire.viseMixte') : t('inventaire.viseBarcode')}</Text>
    </View>
  );
}

function VideVue({ st, onClose, texte, bouton }) {
  const t = useT();
  return (
    <View style={st.vide}>
      <Text style={{ fontSize: 40 }}>📷</Text>
      <Text style={st.videTxt}>{texte}</Text>
      {bouton && (
        <TouchableOpacity style={st.btn} onPress={bouton.onPress}>
          <Text style={st.btnTxt}>{bouton.label}</Text>
        </TouchableOpacity>
      )}
      <TouchableOpacity style={[st.btn, st.btnPlat]} onPress={onClose}>
        <Text style={[st.btnTxt, { color: '#fff' }]}>{t('inventaire.saisirManuel')}</Text>
      </TouchableOpacity>
    </View>
  );
}

const makeStyles = () => StyleSheet.create({
  page:   { backgroundColor: '#000', zIndex: 50 },
  barre:  { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingBottom: 12 },
  // Overlay au-dessus de la caméra : haut de calque, fond dégradé sombre pour
  // la lisibilité, zIndex/elevation pour rester cliquable au-dessus du natif.
  barreOverlay: { position: 'absolute', top: 0, left: 0, right: 0, zIndex: 100, elevation: 30,
                  backgroundColor: 'rgba(0,0,0,0.45)' },
  fermer: { width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.5)' },
  titre:  { color: '#fff', fontSize: 16, fontWeight: '700' },
  camWrap:{ flex: 1, alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  cadre:  { width: '70%', height: 160, borderWidth: 3, borderColor: 'rgba(255,255,255,0.9)', borderRadius: 16 },
  aide:   { position: 'absolute', bottom: 60, color: '#fff', fontSize: 14, fontWeight: '600' },
  vide:   { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32, gap: 16 },
  videTxt:{ color: '#fff', fontSize: 14, textAlign: 'center', lineHeight: 21 },
  btn:    { backgroundColor: '#6C4FE6', borderRadius: 14, paddingVertical: 13, paddingHorizontal: 26 },
  btnPlat:{ backgroundColor: 'transparent', borderWidth: 1, borderColor: 'rgba(255,255,255,0.4)' },
  btnTxt: { color: '#fff', fontWeight: '700', fontSize: 14 },
});
